import {
  getInclusionOptions,
  getOperatorOptions,
  getEventHistoryOperatorOptions,
  getPurchaseHistoryOperatorOptions,
  getMathematicalOperatorOptions
} from '../logicHelpers'

/**
 * Creates condition checking functions for form inputs
 * @param partnerName The name of the partner to check against
 */
const createConditionCheckers = (partnerName: string) => ({
  isTagsOrLists: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName &&
    (condition.$type === 'tags' || condition.$type === 'lists'),

  isCustomField: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'customField',

  isTags: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'tags',

  isLists: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'lists',

  isEventHistory: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'eventHistory',

  isPurchaseHistory: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'purchaseHistory',

  isTotalRevenue: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'totalRevenue'
})

const makePartnerSource = (partner) => {
  const partnerName = partner?.name || 'espplaceholder'
  const conditions = createConditionCheckers(partnerName)

  return {
    types: [
      {
        value: 'isAnonymous',
        label: 'Anonymous'
      },
      {
        value: 'isSubscriber',
        label: 'Subscriber'
      },
      ...(partner?.espFeatureSupport.tag
        ? [
            {
              value: 'tags',
              label: partner?.espFeatureSupport.tag.label || 'Tags'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport.customField
        ? [
            {
              value: 'customField',
              label:
                partner?.espFeatureSupport.customField.label || 'Custom Field'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport.list
        ? [
            {
              value: 'lists',
              label: partner?.espFeatureSupport.list.label || 'Lists'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport?.eventHistory
        ? [
            {
              value: 'eventHistory',
              label: partner?.espFeatureSupport.eventHistory.label || 'Event History'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport?.purchaseHistory
        ? [
            {
              value: 'purchaseHistory',
              label:
                partner?.espFeatureSupport.purchaseHistory.label ||
                'Purchase History'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport?.purchaseHistory?.supportsRevenue
        ? [
            {
              value: 'totalRevenue',
              label: 'Total Revenue'
            }
          ]
        : [])
    ],

    inputs: [
      {
        $formkit: 'select',
        name: 'operator',
        key: 'operator-select',
        options: getInclusionOptions(),
        can: conditions.isTagsOrLists
      },
      {
        $formkit: partner?.espFeatureSupport?.customField?.unstructured
          ? 'text'
          : 'autocomplete',
        name: 'customFieldId',
        key: 'custom-field-field',
        type: 'customFields',
        options: async () => {
          return (
            await getIntegrationData('partner', 'custom_fields')
          ).value.map((field) => ({
            label: field.label,
            value: String(field.id)
          }))
        },
        can: conditions.isCustomField
      },
      {
        $formkit: 'autocomplete',
        name: 'tagIds',
        key: 'tag-ids',
        type: 'tags',
        options: async () => {
          const data = await getIntegrationData('partner', 'tags')
          return data.value.map((field) => ({
            label: field.label,
            value: String(field.id)
          }))
        },
        multiple: true,
        can: conditions.isTags
      },
      {
        $formkit: 'autocomplete',
        name: 'listIds',
        key: 'list-ids',
        options: async () => {
          const data = await getIntegrationData('partner', 'lists')
          return data.value.map((list) => ({
            label: list.label,
            value: String(list.id)
          }))
        },
        multiple: true,
        can: conditions.isLists
      },
      {
        $formkit: 'select',
        key: 'custom-field-operator-select',
        name: 'operator',
        options: getOperatorOptions(),
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === partnerName && condition.$type == 'customField'
      },
      {
        $formkit: 'text',
        key: 'custom-field-value-input',
        name: 'value',
        can: (condition: {
          $source: string
          $type: string
          operator: string
        }) =>
          condition.$source === partnerName &&
          condition.$type === 'customField' &&
          condition.operator !== 'is set' &&
          condition.operator !== 'is not set',
        // The snippet's customField evaluator short-circuits to false when the
        // ESP returns an empty/null value (`partner.js:909`: `!!customFieldValue
        // && ...`). A rule authored with `equals`/`contains`/etc. and a blank
        // value is therefore dead config — it can never match. Surface that in
        // the condition builder so authors don't silently break their own
        // targeting; the correct construct for "field is empty" is the
        // `is not set` operator, which bypasses the guard (see case
        // `'is set'/'is not set'` at `partner.js:907`).
        validate: (value: string | null | undefined) => {
          if (value === undefined || value === null || value === '') {
            return "Blank value won't match. Use the 'is not set' operator to match missing values."
          }
          return null
        }
      },
      // Event History inputs
      {
        $formkit: 'autocomplete',
        name: 'eventName',
        key: 'event-name-select',
        placeholder: 'Select event...',
        options: async () => {
          const data = await getIntegrationData('partner', 'available_events')
          return (data.value || []).map((event: { name: string }) => ({
            label: event.name,
            value: event.name
          }))
        },
        can: conditions.isEventHistory
      },
      {
        $formkit: 'select',
        name: 'operator',
        key: 'event-history-operator-select',
        value: 'has_fired',
        options: getEventHistoryOperatorOptions(),
        can: conditions.isEventHistory
      },
      {
        $formkit: 'number',
        name: 'count',
        key: 'event-count-input',
        placeholder: 'Count',
        min: 0,
        can: (condition: {
          $source: string
          $type: string
          operator: string
        }) =>
          condition.$source === partnerName &&
          condition.$type === 'eventHistory' &&
          ['gte', 'lt', 'equals'].includes(condition.operator)
      },
      // Purchase History inputs - operator first, then product (conditionally)
      {
        $formkit: 'select',
        name: 'operator',
        key: 'purchase-history-operator-select',
        value: 'include any',
        options: getPurchaseHistoryOperatorOptions(),
        can: conditions.isPurchaseHistory
      },
      {
        $formkit: 'autocomplete',
        name: 'productIds',
        key: 'product-ids-select',
        placeholder: 'Select product(s)...',
        options: async () => {
          const data = await getIntegrationData('partner', 'available_products')
          const products = data.value?.products || []
          return products.map((productId: string) => ({
            label: productId,
            value: productId
          }))
        },
        multiple: true,
        // Only show product dropdown for specific product operators (not "is set" / "is not set")
        can: (condition: { $source: string; $type: string; operator: string }) =>
          condition.$source === partnerName &&
          condition.$type === 'purchaseHistory' &&
          ['include any', 'do not include any'].includes(condition.operator)
      },
      // Total Revenue inputs
      {
        $formkit: 'select',
        name: 'operator',
        key: 'total-revenue-operator-select',
        value: 'gte',
        options: getMathematicalOperatorOptions(),
        can: conditions.isTotalRevenue
      },
      {
        $formkit: 'number',
        name: 'value',
        key: 'total-revenue-value-input',
        placeholder: 'Amount',
        min: 0,
        step: 0.01,
        inputClass: 'w-24',
        can: conditions.isTotalRevenue
      }
    ]
  }
}

export default makePartnerSource
